import {
  X,
  Github,
  Star,
  GitFork,
  Copy,
  ExternalLink,
  Check,
  FileText,
  Clock,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Intelligence } from "../../types/intelligence";
import { cn } from "../../utils/cn";
import { useState } from "react";
import { Tooltip } from "../ui/Tooltip";

interface DeepDiveDrawerProps {
  item: Intelligence | null;
  open: boolean;
  onClose: () => void;
}

// Helper function to check if item has GitHub repo
const hasGitHub = (item: Intelligence): boolean =>
  !!(item.github_url && item.github_url.trim() !== "");

// Get recommendation color classes
const getRecommendationColors = (
  score: number,
): { bg: string; text: string; border: string } => {
  if (score >= 70)
    return {
      bg: "bg-emerald-50",
      text: "text-emerald-700",
      border: "border-emerald-200",
    };
  if (score >= 50)
    return {
      bg: "bg-blue-50",
      text: "text-blue-700",
      border: "border-blue-200",
    };
  if (score >= 30)
    return {
      bg: "bg-amber-50",
      text: "text-amber-700",
      border: "border-amber-200",
    };
  return {
    bg: "bg-slate-50",
    text: "text-slate-700",
    border: "border-slate-200",
  };
};

// V3 Dimension configuration
interface V3Dimension {
  key: string;
  labelKey: string;
  tooltipKey: string;
  value: number;
  color: "blue" | "emerald" | "amber" | "purple" | "orange";
  weight: string;
  subScores?: Array<{ key: string; labelKey: string; value: number }>;
}

export function DeepDiveDrawer({ item, open, onClose }: DeepDiveDrawerProps) {
  const { t, i18n } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [expandedDimensions, setExpandedDimensions] = useState<Set<string>>(
    new Set(),
  );

  if (!item) return null;

  const hasRepo = hasGitHub(item);
  const isChinese = i18n.language === "zh";

  // Helper to get localized AI content (fallback to Chinese if English not available)
  const getLocalizedField = (
    zhField?: string,
    enField?: string,
  ): string | undefined => {
    if (isChinese) return zhField;
    return enField || zhField;
  };

  const summary = getLocalizedField(item.ai_summary, item.ai_summary_en);
  const useCasesStr = getLocalizedField(
    item.ai_use_cases,
    item.ai_use_cases_en,
  );
  const techStackStr = getLocalizedField(
    item.ai_tech_stack,
    item.ai_tech_stack_en,
  );
  const integrationEffort = getLocalizedField(
    item.ai_integration_effort,
    item.ai_integration_effort_en,
  );

  const handleCopyClone = async () => {
    if (!hasRepo) return;
    const text = `git clone ${item.github_url}.git`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const getEffortColor = (effort: string | undefined): string => {
    if (!effort) return "bg-slate-100 text-slate-600";
    const e = effort.toLowerCase();
    if (e.includes("low")) return "bg-emerald-100 text-emerald-700";
    if (e.includes("medium") || e.includes("moderate"))
      return "bg-amber-100 text-amber-700";
    if (e.includes("high")) return "bg-red-100 text-red-700";
    return "bg-slate-100 text-slate-600";
  };

  // Toggle dimension expansion
  const toggleDimension = (key: string) => {
    setExpandedDimensions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // V3 Dimensions with sub-scores (GB dimension removed)
  const v3Dimensions: V3Dimension[] = [
    {
      key: "bip",
      labelKey: "scoring.dimensions.bip.title",
      tooltipKey: "drawer.bipDesc",
      value: item.score_v3_bip,
      color: "blue" as any,
      weight: "37%",
      subScores: [
        {
          key: "cost_reduction",
          labelKey: "scoring.subScores.costReduction",
          value: item.score_v3_cost_reduction,
        },
        {
          key: "use_case_value",
          labelKey: "scoring.subScores.useCaseValue",
          value: item.score_v3_use_case_value,
        },
        {
          key: "use_case_breadth",
          labelKey: "scoring.subScores.useCaseBreadth",
          value: item.score_v3_use_case_breadth,
        },
        {
          key: "market_relevance",
          labelKey: "scoring.subScores.marketRelevance",
          value: item.score_v3_market_relevance,
        },
      ],
    },
    {
      key: "tf",
      labelKey: "scoring.dimensions.tf.title",
      tooltipKey: "drawer.tfDesc",
      value: item.score_v3_tf,
      color: "emerald" as any,
      weight: "26%",
      subScores: [
        {
          key: "integration_ease",
          labelKey: "scoring.subScores.integrationEase",
          value: item.score_v3_integration_ease,
        },
        {
          key: "resource_needs",
          labelKey: "scoring.subScores.resourceNeeds",
          value: item.score_v3_resource_needs,
        },
        {
          key: "data_availability",
          labelKey: "scoring.subScores.dataAvailability",
          value: item.score_v3_data_availability,
        },
        {
          key: "complexity",
          labelKey: "scoring.subScores.complexity",
          value: item.score_v3_complexity,
        },
      ],
    },
    {
      key: "cp",
      labelKey: "scoring.dimensions.cp.title",
      tooltipKey: "drawer.cpDesc",
      value: item.score_v3_cp,
      color: "amber" as any,
      weight: "21%",
      subScores: [
        {
          key: "sota_status",
          labelKey: "scoring.subScores.sotaStatus",
          value: item.score_v3_sota_status,
        },
        {
          key: "benchmark_count",
          labelKey: "scoring.subScores.benchmarkCount",
          value: item.score_v3_benchmark_count,
        },
        {
          key: "performance_delta",
          labelKey: "scoring.subScores.performanceDelta",
          value: item.score_v3_performance_delta,
        },
        {
          key: "novelty",
          labelKey: "scoring.subScores.novelty",
          value: item.score_v3_novelty,
        },
      ],
    },
    {
      key: "rr",
      labelKey: "scoring.dimensions.rr.title",
      tooltipKey: "drawer.rrDesc",
      value: item.score_v3_rr,
      color: "purple" as any,
      weight: "16%",
      subScores: [
        {
          key: "citation_score",
          labelKey: "scoring.subScores.citationScore",
          value: item.score_v3_citation_score,
        },
        {
          key: "fwci_score",
          labelKey: "scoring.subScores.fwciScore",
          value: item.score_v3_fwci_score,
        },
        {
          key: "venue_quality",
          labelKey: "scoring.subScores.venueQuality",
          value: item.score_v3_venue_quality,
        },
        {
          key: "method_rigor",
          labelKey: "scoring.subScores.methodRigor",
          value: item.score_v3_method_rigor,
        },
      ],
    },
  ];

  // All dimensions for display (GB removed from scoring)
  const allDimensions = v3Dimensions;

  const allUseCases = useCasesStr
    ? useCasesStr
        .split(";")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

  // Limit to 5 use cases max
  const displayedUseCases = allUseCases.slice(0, 5);
  const remainingCount = allUseCases.length - displayedUseCases.length;

  const techStack = techStackStr
    ? techStackStr
        .split(/[;,]/)
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

  // Use V3 CAV score
  const scoreColors = getRecommendationColors(item.score_v3_cav);

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          "fixed inset-0 bg-black/20 backdrop-blur-sm z-50 transition-opacity duration-200",
          open ? "opacity-100" : "opacity-0 pointer-events-none",
        )}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={cn(
          "fixed right-0 top-0 h-full w-full max-w-xl bg-white shadow-xl z-50 transition-transform duration-200",
          open ? "translate-x-0" : "translate-x-full",
        )}
      >
        <div className="h-full flex flex-col">
          {/* Close Button - Fixed at top right */}
          <div className="absolute right-4 top-4 z-10">
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center transition-colors duration-200 cursor-pointer"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {/* Hero Score Card - Most Prominent */}
            <div
              className={cn(
                "p-6 pt-16",
                scoreColors.bg,
                "border-b",
                scoreColors.border,
              )}
            >
              <div className="flex flex-col items-center text-center">
                {/* Version Badge */}
                <div className="mb-2">
                  <span className="px-2 py-0.5 bg-slate-800 text-white text-xs font-medium rounded">
                    V3
                  </span>
                </div>

                {/* Large Score Display */}
                <div
                  className="text-5xl font-bold num mb-3"
                  style={{
                    color:
                      scoreColors.text.replace("text-", "").split("-")[0] ===
                      "emerald"
                        ? "#059669"
                        : scoreColors.text
                              .replace("text-", "")
                              .split("-")[0] === "blue"
                          ? "#2563eb"
                          : scoreColors.text
                                .replace("text-", "")
                                .split("-")[0] === "amber"
                            ? "#d97706"
                            : "#475569",
                  }}
                >
                  {item.score_v3_cav.toFixed(1)}
                </div>

                <p className="text-sm text-slate-500">{t("drawer.cavFull")}</p>

                {/* Analysis Time */}
                {item.mined_at && (
                  <div className="mt-3 flex items-center gap-1.5 text-xs text-slate-500">
                    <Clock className="w-3.5 h-3.5" />
                    <span>{t("drawer.analysisTime")}:</span>
                    <span className="font-medium">
                      {item.mined_at.split("T")[0]}
                    </span>
                  </div>
                )}

                <p className="text-sm text-slate-500">{t("drawer.cavFull")}</p>

                {/* Analysis Time */}
                {item.mined_at && (
                  <div className="mt-3 flex items-center gap-1.5 text-xs text-slate-500">
                    <Clock className="w-3.5 h-3.5" />
                    <span>{t("drawer.analysisTime")}:</span>
                    <span className="font-medium">
                      {item.mined_at.split("T")[0]}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Project Info Card */}
            <div className="p-4">
              <div className="bg-slate-50 rounded-xl p-4">
                {hasRepo ? (
                  <>
                    {/* Repository Info */}
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 pr-2">
                        <h3 className="font-medium text-slate-800">
                          {item.repo_name}
                        </h3>
                        <p className="text-sm text-slate-500 line-clamp-2">
                          {item.repo_description || t("drawer.noDescription")}
                        </p>
                      </div>
                      <a
                        href={item.github_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-400 hover:text-slate-600 transition-colors duration-200 cursor-pointer"
                      >
                        <Github className="w-5 h-5" />
                      </a>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Star className="w-4 h-4" />
                        {item.repo_stars.toLocaleString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <GitFork className="w-4 h-4" /> {item.repo_forks}
                      </span>
                      {item.repo_language && (
                        <span className="bg-slate-200 text-slate-700 px-2 py-0.5 rounded text-xs">
                          {item.repo_language}
                        </span>
                      )}
                      {item.repo_license && (
                        <span className="text-emerald-600">
                          {item.repo_license}
                        </span>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    {/* Paper-only Info */}
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 pr-2">
                        <div className="flex items-center gap-2 mb-1">
                          <FileText className="w-4 h-4 text-amber-500" />
                          <span className="text-xs text-amber-600 font-medium">
                            {t("drawer.paperOnly")}
                          </span>
                        </div>
                        <h3 className="font-medium text-slate-800 line-clamp-2">
                          {item.paper_title}
                        </h3>
                      </div>
                      <a
                        href={item.paper_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:text-blue-600 transition-colors duration-200 cursor-pointer"
                      >
                        <ExternalLink className="w-5 h-5" />
                      </a>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
                      <p className="text-sm text-amber-700">
                        {t("drawer.noGitHubNote")}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* V3 CAV Dimensions with Collapsible Sub-scores */}
            <div className="px-4 pb-4">
              <div className="bg-slate-50 rounded-xl p-4">
                <h4 className="text-sm font-medium text-slate-700 mb-4">
                  {t("drawer.cavDimensions")}
                </h4>
                <div className="space-y-3">
                  {allDimensions.map((dim) => {
                    const dimension = dim as V3Dimension;
                    const isExpanded = expandedDimensions.has(dimension.key);

                    return (
                      <div
                        key={dimension.key}
                        className="bg-white rounded-lg p-3 border border-slate-200"
                      >
                        <Tooltip
                          content={t(dimension.tooltipKey)}
                          position="top"
                        >
                          <div
                            className="flex items-center justify-between cursor-pointer"
                            onClick={() => toggleDimension(dimension.key)}
                          >
                            <div className="flex items-center gap-2 flex-1">
                              <span className="text-sm font-medium text-slate-700 min-w-[80px]">
                                {t(dimension.labelKey)}
                              </span>
                              <span className="text-xs text-slate-400">
                                {dimension.weight}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-slate-800">
                                {dimension.value.toFixed(1)}
                              </span>
                              {dimension.subScores &&
                                dimension.subScores.length > 0 &&
                                (isExpanded ? (
                                  <ChevronUp className="w-4 h-4 text-slate-400" />
                                ) : (
                                  <ChevronDown className="w-4 h-4 text-slate-400" />
                                ))}
                            </div>
                          </div>
                        </Tooltip>

                        {/* Progress bar */}
                        <div className="mt-2 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className={cn(
                              "h-full rounded-full transition-all duration-300",
                              dimension.color === "blue" && "bg-blue-500",
                              dimension.color === "emerald" && "bg-emerald-500",
                              dimension.color === "amber" && "bg-amber-500",
                              dimension.color === "purple" && "bg-purple-500",
                              dimension.color === "orange" && "bg-orange-500",
                            )}
                            style={{
                              width: `${Math.min(dimension.value, 100)}%`,
                            }}
                          />
                        </div>

                        {/* Sub-scores */}
                        {isExpanded &&
                          dimension.subScores &&
                          dimension.subScores.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
                              <p className="text-xs text-slate-500 mb-2">
                                {t("drawer.subScores")}
                              </p>
                              {dimension.subScores.map((sub) => (
                                <div
                                  key={sub.key}
                                  className="flex items-center justify-between"
                                >
                                  <span className="text-xs text-slate-600">
                                    {t(sub.labelKey)}
                                  </span>
                                  <span className="text-xs font-medium text-slate-700">
                                    {sub.value.toFixed(1)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* AI Insight Section with Gradient */}
            {summary && (
              <div className="px-4 pb-4">
                <div className="bg-gradient-to-b from-blue-50 to-white border border-blue-200 rounded-xl p-4">
                  <h4 className="text-sm font-medium text-blue-800 mb-2 flex items-center gap-2">
                    <span>💡</span>
                    {t("drawer.aiInsight")}
                  </h4>
                  <p className="text-sm text-blue-700">{summary}</p>
                </div>
              </div>
            )}

            {/* Use Cases with Emerald Bullets */}
            {displayedUseCases.length > 0 && (
              <div className="px-4 pb-4">
                <h4 className="text-sm font-medium text-slate-700 mb-2">
                  {t("drawer.useCases")}
                </h4>
                <ul className="space-y-1">
                  {displayedUseCases.map((uc: string, i: number) => (
                    <li
                      key={i}
                      className="text-sm text-slate-600 flex items-start gap-2"
                    >
                      <span className="text-emerald-500 mt-0.5">•</span>
                      {uc}
                    </li>
                  ))}
                </ul>
                {remainingCount > 0 && (
                  <p className="text-sm text-slate-500 mt-2 pl-4">
                    +{remainingCount} {t("common.more")}
                  </p>
                )}
              </div>
            )}

            {/* Integration Effort as Badge */}
            {integrationEffort && (
              <div className="px-4 pb-4">
                <h4 className="text-sm font-medium text-slate-700 mb-2">
                  {t("drawer.integrationEffort")}
                </h4>
                <div>
                  <span
                    className={cn(
                      "px-3 py-1.5 rounded-full text-sm font-medium inline-block cursor-default",
                      getEffortColor(integrationEffort),
                    )}
                  >
                    {integrationEffort}
                  </span>
                </div>
              </div>
            )}

            {/* Tech Stack */}
            {techStack.length > 0 && (
              <div className="px-4 pb-4">
                <h4 className="text-sm font-medium text-slate-700 mb-2">
                  {t("drawer.techStack")}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {techStack.map((tech, i) => (
                    <span
                      key={i}
                      className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="px-4 pb-4">
              <div className="bg-slate-50 rounded-xl p-4">
                <h4 className="text-sm font-medium text-slate-700 mb-3">
                  {t("drawer.quickActions")}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {/* Only show clone button if has GitHub */}
                  {hasRepo && (
                    <button
                      onClick={handleCopyClone}
                      className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors duration-200 text-sm cursor-pointer"
                    >
                      {copied ? (
                        <>
                          <Check className="w-4 h-4" />
                          <span>{t("drawer.copied")}</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          <span>{t("drawer.copyClone")}</span>
                        </>
                      )}
                    </button>
                  )}
                  <a
                    href={item.paper_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 text-sm cursor-pointer"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span>{t("drawer.viewPaper")}</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Paper Link Footer */}
            <div className="p-4 pt-4 border-t border-slate-200">
              <a
                href={item.paper_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 transition-colors duration-200 cursor-pointer"
              >
                <ExternalLink className="w-4 h-4" />
                {t("drawer.viewPaper")}
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
